import type { ComputedRef, Ref } from 'vue';
import type { ResultItem } from '@/utils/file';
import type { ValidData as AdvancedSearchMultiSearchDialogFormValidData } from '@/components/dialogForms/AdvancedSearchMultiSearchDialogForm.vue';
import type { ValidData as SavingCurrentSearchDialogFormValidData } from '@/components/dialogForms/SavingCurrentSearchDialogForm.vue';
import type { Props as SavingCurrentSearchDialogProps } from '@/components/dialogs/SavingCurrentSearchDialog.vue';
import type {
  InitialData as AdvancedSearchSearchFormInitialData,
  ValidData as AdvancedSearchSearchFormValidData,
} from '@/components/searchForms/AdvancedSearchSearchForm.vue';
import type { DataItem as AdvancedSearchTableDataItem } from '@/components/tables/AdvancedSearchTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, toRaw, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isCancel } from 'axios';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';

import { useStaticStore } from '@/stores/useStaticStore';
import { useUserStore } from '@/stores/useUserStore';
import {
  fetchDeleteFilter,
  fetchGetDashboardSearchPdf,
  fetchGetJobSearch,
  fetchGetJobSearchCount,
  fetchPostFilters,
} from '@/helpers/fetchers';
import * as locationQueryHelpers from '@/helpers/locationQuery';
import * as routeQueryHelpers from '@/helpers/routeQuery';
import * as advancedSearchTableHelpers from '@/helpers/tables/advancedSearch';
import * as groupTrackingJobHelpers from '@/helpers/trackingJob/groupTrackingJob';
import * as routeNames from '@/constants/routeNames';

export interface AdvancedSearchViewComposable {
  search: (formData: AdvancedSearchSearchFormValidData) => void;
  refetch: () => Promise<void>;
  initialData: ComputedRef<AdvancedSearchSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  isFetchingCountData: Ref<boolean>;
  isDownloadingPDF: Ref<boolean>;
  total: Ref<number>;
  totalCount: Ref<number | undefined>;
  results: Ref<AdvancedSearchTableDataItem[]>;
  resultsForCsv: Ref<ResultItem[]>;
  downloadPdf: () => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  advancedSearchMultiSearchDialogData: Ref<{}>;
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  savedFiltersDialogData: Ref<{}>;
  savingCurrentSearchDialogData: Ref<
    Required<Pick<SavingCurrentSearchDialogProps, 'confirm'>>
  >;
}

export default function useAdvancedSearchView(
  queryDates: Readonly<Ref<[Date, Date] | undefined>>,
  queryJobIds: Readonly<Ref<number[] | undefined>>,
  queryFilters: Readonly<Ref<string[] | undefined>>,
  queryStates: Readonly<Ref<string[] | undefined>>,
  queryCustomers: Readonly<Ref<string[] | undefined>>,
  queryServices: Readonly<Ref<string[] | undefined>>,
  queryDrivers: Readonly<Ref<number[] | undefined>>,
  querySerial: Readonly<Ref<string | undefined>>,
  queryAddress: Readonly<Ref<string | undefined>>,
  queryCombined: Readonly<Ref<string | undefined>>,
  queryPayOnHold: Readonly<Ref<boolean | undefined>>,
  isStaff: boolean,
): AdvancedSearchViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();
  const staticStore = useStaticStore();

  let controller: AbortController | undefined;
  let countController: AbortController | undefined;

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<AdvancedSearchSearchFormInitialData>(() => {
    return {
      dates: queryDates.value,
      filters: queryFilters.value,
      states: queryStates.value,
      customerCodes: queryCustomers.value,
      serviceCodes: queryServices.value,
      driverIds: queryDrivers.value,
      serialNumber: querySerial.value,
      stopAddress: queryAddress.value,
      combinedData: queryCombined.value,
      payOnHold: queryPayOnHold.value,
    };
  });

  const isFetchingData = ref(false);
  const isFetchingCountData = ref(false);
  const fetchedData = ref<openapi.GetJobSearch_1_200>();
  const fetchedCountData = ref<openapi.GetJobSearch_2_200>();
  const results = ref<AdvancedSearchTableDataItem[]>([]);

  const total = computed(() => {
    return results.value.length;
  });

  const totalCount = computed(() => {
    if (results.value.length < 200) return 0;
    return fetchedCountData.value?.total_count;
  });

  const resultsForCsv = computed<ResultItem[]>(() => {
    return results.value;
  });

  const savedFiltersDialogData = ref({
    deleteCompanyFilter: async (filterId: number) => {
      return await fetchDeleteFilter(filterId);
    },
    deleteMyFilter: async (filterId: number) => {
      return await fetchDeleteFilter(filterId);
    },
  });

  const savingCurrentSearchDialogData = ref({
    confirm: async (formData: SavingCurrentSearchDialogFormValidData) => {
      await fetchPostFilters({
        name: formData.title,
        route: routeNames.SEARCH_ADVANCED,
        type: formData.filterType,
        params: { ...route.query, dates: undefined },
      });
    },
  });

  const advancedSearchMultiSearchDialogData = ref({
    confirm: async (formData: AdvancedSearchMultiSearchDialogFormValidData) => {
      let query;
      if (formData.dataType === 'jobIds') {
        const jobIds =
          routeQueryHelpers.getQueryNumberListValue(formData.data) ?? [];
        query = { ...route.query, job_ids: jobIds.join(',') };
      } else if (formData.dataType === 'serialNumber') {
        query = { ...route.query, serial: formData.data };
      } else if (formData.dataType === 'combinedData') {
        query = { ...route.query, combined: formData.data };
      } else if (formData.dataType === 'combinedAddress') {
        query = { ...route.query, address: formData.data };
      }
      const isReplace = _isEqual(route.query, query);
      void router.push({
        name: routeNames.SEARCH_ADVANCED,
        query,
        force: true,
        replace: isReplace,
      });
    },
  });

  function search(formData: AdvancedSearchSearchFormValidData): void {
    const query = locationQueryHelpers.buildAdvancedSearchSearchLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.SEARCH_ADVANCED,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function clear(): void {
    if (controller) controller.abort();
    if (countController) countController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function fetchData(): Promise<void> {
    const dates = queryDates.value;
    const jobIds = queryJobIds.value;
    const filters = queryFilters.value;
    const states = queryStates.value;
    const customerCodes = queryCustomers.value;
    const serviceCodes = queryServices.value;
    const driverIds = queryDrivers.value;
    const serialNumber = querySerial.value;
    const stopAddress = queryAddress.value;
    const combinedData = queryCombined.value;
    const payOnHold = queryPayOnHold.value;
    fetchedData.value = undefined;
    if (controller) controller.abort();
    if (
      !dates?.length ||
      !(
        jobIds?.length ||
        states?.length ||
        customerCodes?.length ||
        serviceCodes?.length ||
        driverIds?.length ||
        serialNumber ||
        stopAddress ||
        combinedData ||
        payOnHold != null
      )
    ) {
      return;
    }
    isFetchingData.value = true;
    controller = new AbortController();
    try {
      const data = await fetchGetJobSearch(
        userTimeZone.value,
        {
          dates,
          jobIds,
          filters,
          states,
          customerCodes,
          serviceCodes,
          driverIds,
          serialNumber,
          stopAddress,
          combinedData,
          payOnHold,
        },
        {
          signal: controller.signal,
        },
      );
      fetchedData.value = data;
    } catch (err: unknown) {
      if (!isCancel(err)) throw err;
    } finally {
      isFetchingData.value = false;
    }
  }

  async function fetchCountData(): Promise<void> {
    const dates = queryDates.value;
    const jobIds = queryJobIds.value;
    const filters = queryFilters.value;
    const states = queryStates.value;
    const customerCodes = queryCustomers.value;
    const serviceCodes = queryServices.value;
    const driverIds = queryDrivers.value;
    const serialNumber = querySerial.value;
    const stopAddress = queryAddress.value;
    const combinedData = queryCombined.value;
    const payOnHold = queryPayOnHold.value;
    fetchedCountData.value = undefined;
    if (countController) countController.abort();
    if (
      !dates?.length ||
      !(
        jobIds?.length ||
        states?.length ||
        customerCodes?.length ||
        serviceCodes?.length ||
        driverIds?.length ||
        serialNumber ||
        stopAddress ||
        combinedData
      )
    ) {
      return;
    }
    isFetchingCountData.value = true;
    countController = new AbortController();
    try {
      const data = await fetchGetJobSearchCount(
        userTimeZone.value,
        {
          dates,
          jobIds,
          filters,
          states,
          customerCodes,
          serviceCodes,
          driverIds,
          serialNumber,
          stopAddress,
          combinedData,
          payOnHold,
          count: true,
        },
        {
          signal: countController.signal,
        },
      );
      fetchedCountData.value = data;
    } catch (err: unknown) {
      if (!isCancel(err)) throw err;
    } finally {
      isFetchingCountData.value = false;
    }
  }

  function updateResults(): void {
    if (!fetchedData.value) {
      results.value = [];
      return;
    }
    const categoryJobs = groupTrackingJobHelpers.getCategoryJobs(
      _cloneDeep(toRaw(fetchedData.value)),
      userTimeZone.value,
    );
    results.value = fetchedData.value.map((job) => {
      return advancedSearchTableHelpers.getTableItem(
        job,
        isStaff,
        categoryJobs,
        staticStore.getBases,
      );
    });
  }

  watch(
    [
      queryDates,
      queryJobIds,
      queryFilters,
      queryStates,
      queryCustomers,
      queryServices,
      queryDrivers,
      querySerial,
      queryAddress,
      queryCombined,
    ],
    async (newValues, oldValues) => {
      if (route.name !== routeNames.SEARCH_ADVANCED) return;
      await fetchData();
      updateResults();
      if (results.value.length === 200) {
        await fetchCountData();
      }
    },
    { immediate: true, deep: true },
  );

  async function refetch(): Promise<void> {
    await fetchData();
    updateResults();
  }

  const isDownloadingPDF = ref(false);

  async function downloadPdf(): Promise<void> {
    if (!queryDates.value) return;
    isDownloadingPDF.value = true;
    try {
      await fetchGetDashboardSearchPdf(userTimeZone.value, {
        dates: queryDates.value,
        jobIds: queryJobIds.value,
        filters: queryFilters.value,
        states: queryStates.value,
        customerCodes: queryCustomers.value,
        serviceCodes: queryServices.value,
        driverIds: queryDrivers.value,
        serialNumber: querySerial.value,
        stopAddress: queryAddress.value,
        combinedData: queryCombined.value,
        payOnHold: queryPayOnHold.value,
      });
    } finally {
      isDownloadingPDF.value = false;
    }
  }

  return {
    search,
    refetch,
    initialData,
    isFetchingData,
    isFetchingCountData,
    isDownloadingPDF,
    total,
    totalCount,
    results,
    resultsForCsv,
    downloadPdf,
    advancedSearchMultiSearchDialogData,
    savedFiltersDialogData,
    savingCurrentSearchDialogData,
  };
}
